/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculate relative luminance of a color
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const val = c / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */
export function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) {
    return 1; // Return minimum contrast if colors are invalid
  }

  const l1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if color combination meets WCAG AA standards
 * WCAG AA requires:
 * - Normal text: 4.5:1
 * - Large text (18pt+): 3:1
 *
 * We use the normal text threshold for tier labels
 */
export function meetsAccessibilityStandards(
  textColor: string,
  backgroundColor: string
): boolean {
  const contrastRatio = getContrastRatio(textColor, backgroundColor);
  return contrastRatio >= 4.5;
}

/**
 * Get accessibility level for a color combination
 */
export function getAccessibilityLevel(
  textColor: string,
  backgroundColor: string
): { level: "AAA" | "AA" | "fail"; ratio: number } {
  const ratio = getContrastRatio(textColor, backgroundColor);

  if (ratio >= 7) {
    return { level: "AAA", ratio };
  } else if (ratio >= 4.5) {
    return { level: "AA", ratio };
  } else {
    return { level: "fail", ratio };
  }
}
